import { Field, Form, Formik } from 'formik';
import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { Button, Fieldset, Input, Textarea, Upload, UploadedAsset } from '@fourthwall/components';
import * as S from './BasicInformationTab.styled';
import { CredentialDisplay } from './components/CredentialDisplay/CredentialDisplay';
import { useUpdatePlatformAppClientDetails } from './hooks/useUpdatePlatformAppClientDetails';
import { useUpdatePlatformAppClientIcon } from './hooks/useUpdatePlatformAppClientIcon';
const validationSchema = Yup.object().shape({
    name: Yup.string().required('App name is required'),
    description: Yup.string(),
    settingsUrl: Yup.string().url('Must be a valid URL').optional().nullable(),
    learnMoreUrl: Yup.string().url('Must be a valid URL').optional().nullable(),
    icon: Yup.string(),
});
const getInitialValues = (app) => ({
    name: app?.name || '',
    description: app?.description || '',
    settingsUrl: app?.settingsUrl,
    learnMoreUrl: app?.learnMoreUrl,
    icon: app?.iconUrl || '',
});
export const BasicInformationTab = ({ app }) => {
    const { updatePlatformAppClientDetails, isLoading } = useUpdatePlatformAppClientDetails();
    const { updatePlatformAppClientIcon, isLoading: isIconLoading } = useUpdatePlatformAppClientIcon();
    const handleSubmit = (values) => {
        if (!app.id)
            return;
        updatePlatformAppClientDetails([
            { clientId: app.id },
            {
                displayName: values.name,
                description: values.description,
                settingsUrl: values.settingsUrl,
                learnMoreUrl: values.learnMoreUrl,
            },
        ]);
    };
    return (React.createElement(Formik, { initialValues: getInitialValues(app), validationSchema: validationSchema, onSubmit: handleSubmit }, ({ submitForm, setFieldValue, values }) => {
        useEffect(() => {
            setFieldValue('icon', app.iconUrl || '');
        }, [app.iconUrl, setFieldValue]);
        return (React.createElement(Form, null,
            React.createElement(S.Container, null,
                React.createElement(Fieldset, null,
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(Field, { component: Input, name: "name", label: "App Name", placeholder: "Enter app name" }))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement("div", null,
                                React.createElement(S.Label, { htmlFor: "description__textarea" }, "Description"),
                                React.createElement(Field, { component: Textarea, rows: 3, name: "description", label: "Description", placeholder: "Enter app description" })))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(Field, { component: Input, name: "settingsUrl", label: "Settings URL", placeholder: "https://your-app.com/settings" }))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(Field, { component: Input, name: "learnMoreUrl", label: "Learn More URL", placeholder: "https://your-app.com/learn-more" }))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(S.Label, null, "App Icon"),
                            values.icon ? (React.createElement("div", { style: { width: '200px' } },
                                React.createElement(UploadedAsset, { id: "app-icon", src: values.icon, onRemove: () => setFieldValue('icon', '') },
                                    React.createElement(S.IconContainer, null,
                                        React.createElement(S.IconPreview, { src: app.iconUrl, alt: "App icon" }))))) : (React.createElement(Upload, { accept: {
                                    'image/jpeg': ['.jpg', '.jpeg'],
                                    'image/png': ['.png'],
                                    'image/svg+xml': ['.svg'],
                                    'image/webp': ['.webp'],
                                }, title: "Drop icon or browse", size: "medium", maxSize: 524288, isUploading: isIconLoading, onFileDrop: async (files) => {
                                    if (files?.[0]) {
                                        updatePlatformAppClientIcon([{ clientId: app.id }, files[0]]);
                                    }
                                } })),
                            React.createElement(S.IconInfo, null, "Recommended: Square image, at least 200x200px. Max size: 512KB."))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(S.SectionLabel, null, "Credentials"))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(CredentialDisplay, { label: "HMAC Key", copyable: true, onReveal: async () => {
                                    return app.hmacKey || '';
                                } })))),
                React.createElement(S.ButtonContainer, null,
                    React.createElement(Button, { label: "Save Changes", appearance: "primary", onClick: submitForm, disabled: isLoading, loading: isLoading })))));
    }));
};
